import { addErrorInDatadogRum } from './utilities';
import { createClient } from '../services/client';
import LogPaymentSecurityEvent from '../graphql/logPaymentSecurityEvent';

/**
 * Payment Security Tracking Utility
 * 
 * This utility helps track payment attempts to detect potential carding attacks
 * and suspicious user behavior patterns.
 * 
 * Logs to: Console (structured), DataDog RUM, and optionally to server endpoint
 */

// Constants for tracking payment security events
const PAYMENT_SECURITY_EVENTS = {
  PAYMENT_ATTEMPT: 'payment_attempt',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILURE: 'payment_failure',
  SUSPICIOUS_ACTIVITY: 'suspicious_payment_activity',
  RAPID_PAYMENT_ATTEMPTS: 'rapid_payment_attempts',
  MULTIPLE_CARD_FAILURES: 'multiple_card_failures',
  TOKEN_REUSE_DETECTED: 'chargify_token_reuse_detected',
  TOKEN_REUSE_ATTEMPT: 'chargify_token_reuse_attempt'
};

/**
 * Enhanced logging function that sends to multiple destinations
 */
const logSecurityEvent = (eventType, eventData) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    event_type: eventType,
    ...eventData,
    source: 'payment_security_tracker'
  };

  // Console logging removed for UAT/production - events are logged to DataDog and server endpoint

  // 2. DataDog RUM logging for monitoring
  try {
    addErrorInDatadogRum({
      type: 'payment_security_event',
      event_type: eventType,
      data: logEntry
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to log to DataDog:', error);
  }

  // 3. Optional: Send to server endpoint for centralized logging
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    sendToServerLog(logEntry).catch(() => {
      // Silently fail - don't interrupt payment flow
    });
  }

  return logEntry;
};

// ============================================================================
// PAYMENT PAGE INTERACTION TRACKING FOR reCAPTCHA ENTERPRISE
// ============================================================================

// Payment page interaction types for reCAPTCHA Enterprise
export const PAYMENT_INTERACTIONS = {
  PAGE_LOAD: 'payment_page_load',
  PAGE_REFRESH: 'payment_page_refresh',
  PAGE_VISIBILITY_CHANGE: 'payment_page_visibility_change',
  CARD_NUMBER_CHANGE: 'card_number_change',
  CARD_NUMBER_PASTE: 'card_number_paste',
  CVV_CHANGE: 'cvv_change',
  EXPIRY_CHANGE: 'expiry_change',
  PAYMENT_METHOD_SWITCH: 'payment_method_switch',
  BILLING_ADDRESS_CHANGE: 'billing_address_change',
  FORM_FOCUS: 'payment_form_focus',
  FORM_BLUR: 'payment_form_blur',
  FORM_VALIDATION_ERROR: 'form_validation_error'
};

// Suspicious behavior thresholds
const SUSPICIOUS_THRESHOLDS = {
  PAGE_REFRESHES: 5,
  CARD_NUMBER_CHANGES: 10,
  CVV_CHANGES: 8,
  PAYMENT_METHOD_SWITCHES: 3,
  RAPID_INTERACTION_MS: 100,
  SESSION_DURATION_SECONDS: 30 // Completing form too quickly
};

// Blocking thresholds for bot detection
const BLOCKING_THRESHOLDS = {
  RAPID_CVV_RETRIES: 3, // CVV changes in time window
  RAPID_CVV_WINDOW_MS: 5000, // 5 seconds
  AUTO_FILL_MAX_TIME_MS: 2000, // All fields in <2 seconds
  FIELD_COMPLETION_MAX_MS: 500, // Between fields
  CRITICAL_CARD_CHANGES: 10, // Block after this many
  CRITICAL_CVV_CHANGES: 8, // Block after this many
  MAX_FAILED_ATTEMPTS: 3, // Block after 3 failed attempts
  FAILED_ATTEMPT_WINDOW_MS: 10 * 60 * 1000, // only failures within this window count
};

/**
 * Get payment interaction session data from sessionStorage
 */
// The block message tells users to refresh; sessionStorage survives refreshes,
// so without this a blocked user could never recover in the same tab.
let blockClearedThisPageLoad = false;

const getInteractionSessionData = () => {
  try {
    const data = sessionStorage.getItem('paymentInteractionSession');
    const session = data ? JSON.parse(data) : initializeSessionData();
    if (!blockClearedThisPageLoad) {
      blockClearedThisPageLoad = true;
      if (session.isBlocked) {
        session.isBlocked = false;
        session.blockReason = null;
        session.failedAttempts = 0;
        session.failedAttemptTimestamps = [];
        saveInteractionSessionData(session);
      }
    }
    return session;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to get interaction session', error);
    return initializeSessionData();
  }
};

/**
 * Initialize new session data
 */
const initializeSessionData = () => ({
  sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  sessionStartTime: Date.now(),
  interactions: {},
  interactionTimestamps: [],
  lastInteractionTime: null,
  lastInteractionType: null,
  suspiciousEvents: [],
  recaptchaTokensGenerated: 0,
  // Enhanced tracking for blocking
  cvvChangeTimestamps: [], // Track CVV change times for rapid retry detection
  fieldCompletionTimes: {}, // Track when each field was completed for auto-fill detection
  failedAttempts: 0, // Track consecutive failed payment attempts
  failedAttemptTimestamps: [], // When each failure happened, for windowed counting
  isBlocked: false, // Flag if session is blocked
  blockReason: null, // Reason for blocking
  requiresRecaptchaChallenge: false // Flag if reCAPTCHA challenge required
});

/**
 * Save interaction session data to sessionStorage
 */
const saveInteractionSessionData = (data) => {
  try {
    sessionStorage.setItem('paymentInteractionSession', JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save interaction session', error);
  }
};

/**
 * Clear interaction session (call after successful payment or page unload)
 */
export const clearInteractionSession = () => {
  try {
    sessionStorage.removeItem('paymentInteractionSession');
  } catch (error) {
    // Silently fail - session clearing is not critical
    addErrorInDatadogRum(error);
  }
};

/**
 * Detect suspicious patterns in user behavior
 */
const detectSuspiciousPatterns = (sessionData, timeSinceLastInteraction) => {
  const patterns = [];
  const { interactions } = sessionData;
  
  // Pattern 1: Excessive page refreshes
  if (interactions[PAYMENT_INTERACTIONS.PAGE_REFRESH] >= SUSPICIOUS_THRESHOLDS.PAGE_REFRESHES) {
    patterns.push({
      type: 'excessive_page_refreshes',
      severity: 'high',
      count: interactions[PAYMENT_INTERACTIONS.PAGE_REFRESH],
      threshold: SUSPICIOUS_THRESHOLDS.PAGE_REFRESHES
    });
  }
  
  // Pattern 2: Excessive card number changes (potential carding)
  if (interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE] >= SUSPICIOUS_THRESHOLDS.CARD_NUMBER_CHANGES) {
    patterns.push({
      type: 'excessive_card_changes',
      severity: 'critical',
      count: interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE],
      threshold: SUSPICIOUS_THRESHOLDS.CARD_NUMBER_CHANGES,
      description: 'Potential carding attack'
    });
  }
  
  // Pattern 3: Excessive CVV changes
  if (interactions[PAYMENT_INTERACTIONS.CVV_CHANGE] >= SUSPICIOUS_THRESHOLDS.CVV_CHANGES) {
    patterns.push({
      type: 'excessive_cvv_changes',
      severity: 'high',
      count: interactions[PAYMENT_INTERACTIONS.CVV_CHANGE],
      threshold: SUSPICIOUS_THRESHOLDS.CVV_CHANGES
    });
  }
  
  // Pattern 4: Excessive payment method switching
  if (interactions[PAYMENT_INTERACTIONS.PAYMENT_METHOD_SWITCH] >= SUSPICIOUS_THRESHOLDS.PAYMENT_METHOD_SWITCHES) {
    patterns.push({
      type: 'excessive_method_switching',
      severity: 'medium',
      count: interactions[PAYMENT_INTERACTIONS.PAYMENT_METHOD_SWITCH],
      threshold: SUSPICIOUS_THRESHOLDS.PAYMENT_METHOD_SWITCHES
    });
  }
  
  // Pattern 5: Rapid interactions (bot-like behavior)
  if (timeSinceLastInteraction !== null && 
      timeSinceLastInteraction < SUSPICIOUS_THRESHOLDS.RAPID_INTERACTION_MS) {
    patterns.push({
      type: 'rapid_interactions',
      severity: 'medium',
      time_between_ms: timeSinceLastInteraction,
      threshold: SUSPICIOUS_THRESHOLDS.RAPID_INTERACTION_MS,
      description: 'Bot-like interaction speed'
    });
  }
  
  // Pattern 6: Completing form too quickly (automated)
  const sessionDuration = (Date.now() - sessionData.sessionStartTime) / 1000;
  const totalInteractions = Object.values(interactions).reduce((a, b) => a + b, 0);
  if (sessionDuration < SUSPICIOUS_THRESHOLDS.SESSION_DURATION_SECONDS && totalInteractions > 5) {
    patterns.push({
      type: 'form_completed_too_quickly',
      severity: 'medium',
      session_duration_seconds: sessionDuration,
      threshold: SUSPICIOUS_THRESHOLDS.SESSION_DURATION_SECONDS,
      description: 'Automated form filling suspected'
    });
  }
  
  // Pattern 7: High paste activity (stolen card data)
  if (interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_PASTE] >= 2) {
    patterns.push({
      type: 'frequent_paste_activity',
      severity: 'low',
      count: interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_PASTE],
      description: 'Potential stolen card data'
    });
  }
  
  return patterns;
};

/**
 * Determine if suspicious behavior warrants generating a new reCAPTCHA token
 */
export const shouldGenerateRecaptchaToken = (sessionData, suspiciousPatterns = []) => {
  const { interactions } = sessionData;
  
  // Generate token if critical patterns detected
  const hasCriticalPattern = suspiciousPatterns.some(p => p.severity === 'critical');
  if (hasCriticalPattern) return true;
  
  // Generate token if multiple high-severity patterns
  const highSeverityCount = suspiciousPatterns.filter(p => p.severity === 'high').length;
  if (highSeverityCount >= 2) return true;
  
  // Generate token based on specific thresholds
  return (
    interactions[PAYMENT_INTERACTIONS.PAGE_REFRESH] > 3 ||
    interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE] > 5 ||
    interactions[PAYMENT_INTERACTIONS.PAYMENT_METHOD_SWITCH] > 2 ||
    suspiciousPatterns.length > 3
  );
};

/**
 * Get session summary to send with reCAPTCHA token
 */
export const getSessionSummary = (sessionData = null) => {
  const data = sessionData || getInteractionSessionData();
  
  return {
    session_id: data.sessionId,
    session_duration_seconds: Math.floor((Date.now() - data.sessionStartTime) / 1000),
    total_interactions: Object.values(data.interactions).reduce((a, b) => a + b, 0),
    interaction_counts: data.interactions,
    suspicious_event_count: data.suspiciousEvents.length,
    recaptcha_tokens_generated: data.recaptchaTokensGenerated,
    has_suspicious_activity: data.suspiciousEvents.length > 0
  };
};

/**
 * Track a payment page interaction
 * @param {string} interactionType - Type of interaction from PAYMENT_INTERACTIONS
 * @param {object} metadata - Additional metadata about the interaction
 * @returns {object} Analysis result with suspicious flags
 */
export const trackPaymentPageInteraction = (interactionType, metadata = {}) => {
  const sessionData = getInteractionSessionData();
  const timestamp = Date.now();
  
  // Increment interaction counter
  sessionData.interactions[interactionType] = (sessionData.interactions[interactionType] || 0) + 1;
  
  // Track timing
  sessionData.interactionTimestamps.push({ type: interactionType, time: timestamp });
  const timeSinceLastInteraction = sessionData.lastInteractionTime 
    ? timestamp - sessionData.lastInteractionTime 
    : null;
  
  sessionData.lastInteractionTime = timestamp;
  sessionData.lastInteractionType = interactionType;
  
  // Enhanced tracking for blocking detection
  // Track CVV change timestamps for rapid retry detection
  if (interactionType === PAYMENT_INTERACTIONS.CVV_CHANGE) {
    if (!sessionData.cvvChangeTimestamps) {
      sessionData.cvvChangeTimestamps = [];
    }
    sessionData.cvvChangeTimestamps.push(timestamp);
    // Keep only recent CVV changes (within last 10 seconds)
    const tenSecondsAgo = timestamp - 10000;
    sessionData.cvvChangeTimestamps = sessionData.cvvChangeTimestamps.filter(
      ts => ts > tenSecondsAgo
    );
  }
  
  // Track field completion times for auto-fill detection
  const fieldTypes = {
    [PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE]: 'cardNumber',
    [PAYMENT_INTERACTIONS.EXPIRY_CHANGE]: 'expiry',
    [PAYMENT_INTERACTIONS.CVV_CHANGE]: 'cvv',
  };
  
  if (fieldTypes[interactionType]) {
    if (!sessionData.fieldCompletionTimes) {
      sessionData.fieldCompletionTimes = {};
    }
    sessionData.fieldCompletionTimes[fieldTypes[interactionType]] = timestamp;
  }
  
  // Detect suspicious patterns
  const suspiciousPatterns = detectSuspiciousPatterns(sessionData, timeSinceLastInteraction);
  
  // Progressive blocking: Tier 1 - Warning (log suspicious activity)
  if (suspiciousPatterns.length > 0) {
    sessionData.suspiciousEvents.push({
      timestamp,
      interactionType,
      patterns: suspiciousPatterns
    });
    
    // Tier 1: Warning - Log suspicious activity (already done via logSecurityEvent below)
    // Tier 2: Challenge - Check if reCAPTCHA challenge should be required
    const hasHighSeverityPattern = suspiciousPatterns.some(p => p.severity === 'high' || p.severity === 'critical');
    if (hasHighSeverityPattern && !sessionData.requiresRecaptchaChallenge) {
      sessionData.requiresRecaptchaChallenge = true;
    }
  }
  
  // Save updated session
  saveInteractionSessionData(sessionData);
  
  // Determine if should generate new reCAPTCHA token
  const shouldGenerateToken = shouldGenerateRecaptchaToken(sessionData, suspiciousPatterns);
  
  // Log to security events
  if (suspiciousPatterns.length > 0) {
    logSecurityEvent(PAYMENT_SECURITY_EVENTS.SUSPICIOUS_ACTIVITY, {
      interaction_type: interactionType,
      interaction_count: sessionData.interactions[interactionType],
      suspicious_patterns: suspiciousPatterns,
      session_id: sessionData.sessionId,
      session_duration_seconds: Math.floor((timestamp - sessionData.sessionStartTime) / 1000),
      total_interactions: Object.values(sessionData.interactions).reduce((a, b) => a + b, 0),
      is_suspicious: true,
      ...metadata
    });
  }
  
  // Log to DataDog
  addErrorInDatadogRum({
    type: 'payment_page_interaction',
    interaction_type: interactionType,
    interaction_count: sessionData.interactions[interactionType],
    session_id: sessionData.sessionId,
    is_suspicious: suspiciousPatterns.length > 0,
    suspicious_patterns: suspiciousPatterns,
    should_generate_recaptcha: shouldGenerateToken,
    ...metadata
  });
  
  return {
    isSuspicious: suspiciousPatterns.length > 0,
    patterns: suspiciousPatterns,
    shouldGenerateRecaptcha: shouldGenerateToken,
    sessionSummary: getSessionSummary(sessionData)
  };
};

/**
 * Check for rapid CVV retry pattern (bot behavior)
 * Detects 3+ CVV changes within 5 seconds
 */
const checkRapidCvvRetries = (sessionData) => {
  if (!sessionData.cvvChangeTimestamps || sessionData.cvvChangeTimestamps.length < BLOCKING_THRESHOLDS.RAPID_CVV_RETRIES) {
    return null;
  }
  
  const now = Date.now();
  const recentCvvChanges = sessionData.cvvChangeTimestamps.filter(
    ts => (now - ts) <= BLOCKING_THRESHOLDS.RAPID_CVV_WINDOW_MS
  );
  
  if (recentCvvChanges.length >= BLOCKING_THRESHOLDS.RAPID_CVV_RETRIES) {
    // Check if changes are rapid (within time window)
    const sortedChanges = recentCvvChanges.sort((a, b) => a - b);
    const timeSpan = sortedChanges[sortedChanges.length - 1] - sortedChanges[0];
    
    if (timeSpan <= BLOCKING_THRESHOLDS.RAPID_CVV_WINDOW_MS) {
      return {
        type: 'rapid_cvv_retries',
        severity: 'critical',
        count: recentCvvChanges.length,
        timeSpan,
        threshold: BLOCKING_THRESHOLDS.RAPID_CVV_RETRIES,
        description: 'Bot detected: Rapid CVV retry pattern'
      };
    }
  }
  
  return null;
};

/**
 * Check for auto-fill pattern (bot behavior)
 * Detects fields completed too quickly (<500ms between fields, <2 seconds total)
 */
const checkAutoFillPattern = (sessionData) => {
  if (!sessionData.fieldCompletionTimes) {
    return null;
  }
  
  const { cardNumber, expiry, cvv } = sessionData.fieldCompletionTimes;
  
  // Need at least card number and CVV to detect auto-fill
  if (!cardNumber || !cvv) {
    return null;
  }
  
  const completionOrder = [];
  if (cardNumber) completionOrder.push({ field: 'cardNumber', time: cardNumber });
  if (expiry) completionOrder.push({ field: 'expiry', time: expiry });
  if (cvv) completionOrder.push({ field: 'cvv', time: cvv });
  
  if (completionOrder.length < 2) {
    return null;
  }
  
  // Sort by completion time
  completionOrder.sort((a, b) => a.time - b.time);
  
  // Check time between fields
  const timeBetweenFields = [];
  for (let i = 1; i < completionOrder.length; i++) {
    const timeDiff = completionOrder[i].time - completionOrder[i - 1].time;
    timeBetweenFields.push(timeDiff);
  }
  
  // Check total time to complete all fields
  const totalTime = completionOrder[completionOrder.length - 1].time - completionOrder[0].time;
  
  // Detect auto-fill: very fast between fields AND total time is very short
  const hasRapidFieldCompletion = timeBetweenFields.some(
    diff => diff < BLOCKING_THRESHOLDS.FIELD_COMPLETION_MAX_MS
  );
  const isVeryFastTotal = totalTime < BLOCKING_THRESHOLDS.AUTO_FILL_MAX_TIME_MS;
  
  if (hasRapidFieldCompletion && isVeryFastTotal) {
    return {
      type: 'auto_fill_detected',
      severity: 'high',
      totalTime,
      timeBetweenFields,
      threshold: BLOCKING_THRESHOLDS.AUTO_FILL_MAX_TIME_MS,
      description: 'Bot detected: Auto-fill pattern (fields completed too quickly)'
    };
  }
  
  return null;
};

/**
 * Check failed attempt rate
 */
const checkFailedAttemptRate = (sessionData) => {
  // Count only recent failures: a shopper whose card declined a few times over
  // a long session is not a brute-force pattern, three failures in quick
  // succession is.
  const windowStart = Date.now() - BLOCKING_THRESHOLDS.FAILED_ATTEMPT_WINDOW_MS;
  const timestamps = sessionData.failedAttemptTimestamps || [];
  const failedAttempts = timestamps.length
    ? timestamps.filter((t) => t >= windowStart).length
    : sessionData.failedAttempts || 0;

  if (failedAttempts >= BLOCKING_THRESHOLDS.MAX_FAILED_ATTEMPTS) {
    return {
      type: 'excessive_failed_attempts',
      severity: 'critical',
      count: failedAttempts,
      threshold: BLOCKING_THRESHOLDS.MAX_FAILED_ATTEMPTS,
      description: 'Too many failed payment attempts - potential brute force attack'
    };
  }
  
  return null;
};

/**
 * Get blocking reason from session data
 * Returns object with blocking information or null if no blocking needed
 */
export const getBlockReason = (sessionData = null) => {
  const data = sessionData || getInteractionSessionData();
  const { interactions } = data;
  
  // Check if already blocked
  if (data.isBlocked && data.blockReason) {
    return {
      shouldBlock: true,
      reason: data.blockReason,
      severity: 'critical'
    };
  }
  
  // Check critical patterns that should always block
  // 1. Excessive card changes (carding attack)
  if (interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE] >= BLOCKING_THRESHOLDS.CRITICAL_CARD_CHANGES) {
    return {
      shouldBlock: true,
      reason: 'excessive_card_changes',
      severity: 'critical',
      count: interactions[PAYMENT_INTERACTIONS.CARD_NUMBER_CHANGE],
      message: 'For security reasons, we cannot process this payment. Please refresh the page and try again.'
    };
  }
  
  // 2. Excessive CVV changes
  if (interactions[PAYMENT_INTERACTIONS.CVV_CHANGE] >= BLOCKING_THRESHOLDS.CRITICAL_CVV_CHANGES) {
    return {
      shouldBlock: true,
      reason: 'excessive_cvv_changes',
      severity: 'critical',
      count: interactions[PAYMENT_INTERACTIONS.CVV_CHANGE],
      message: 'For security reasons, we cannot process this payment. Please refresh the page and try again.'
    };
  }
  
  // 3. Rapid CVV retries
  const rapidCvvPattern = checkRapidCvvRetries(data);
  if (rapidCvvPattern) {
    return {
      shouldBlock: true,
      reason: 'rapid_cvv_retries',
      severity: 'critical',
      pattern: rapidCvvPattern,
      message: 'For security reasons, we cannot process this payment. Please refresh the page and try again.'
    };
  }
  
  // 4. Failed attempt rate
  const failedAttemptPattern = checkFailedAttemptRate(data);
  if (failedAttemptPattern) {
    return {
      shouldBlock: true,
      reason: 'excessive_failed_attempts',
      severity: 'critical',
      pattern: failedAttemptPattern,
      message: 'For security reasons, we cannot process this payment. Please refresh the page and try again.'
    };
  }
  
  // Check for patterns that require reCAPTCHA challenge (progressive blocking)
  const autoFillPattern = checkAutoFillPattern(data);
  if (autoFillPattern) {
    return {
      shouldBlock: false,
      requiresChallenge: true,
      reason: 'auto_fill_detected',
      severity: 'high',
      pattern: autoFillPattern,
      message: 'Please complete the security verification to continue.'
    };
  }
  
  return null;
};

/**
 * Determine if submission should be blocked
 * Returns true if submission should be completely blocked
 */
export const shouldBlockSubmission = (sessionData = null) => {
  const blockReason = getBlockReason(sessionData);
  return blockReason?.shouldBlock === true;
};

/**
 * Check if reCAPTCHA challenge is required (progressive blocking)
 */
export const requiresRecaptchaChallenge = (sessionData = null) => {
  const blockReason = getBlockReason(sessionData);
  return blockReason?.requiresChallenge === true;
};

/**
 * Increment failed attempt counter
 * Call this when a payment attempt fails
 */
export const incrementFailedAttempts = () => {
  const sessionData = getInteractionSessionData();
  sessionData.failedAttempts = (sessionData.failedAttempts || 0) + 1;
  sessionData.failedAttemptTimestamps = [
    ...(sessionData.failedAttemptTimestamps || []),
    Date.now(),
  ];
  saveInteractionSessionData(sessionData);
  
  // Check if should block after incrementing
  const failedAttemptPattern = checkFailedAttemptRate(sessionData);
  if (failedAttemptPattern) {
    sessionData.isBlocked = true;
    sessionData.blockReason = 'excessive_failed_attempts';
    saveInteractionSessionData(sessionData);
    
    logSecurityEvent(PAYMENT_SECURITY_EVENTS.SUSPICIOUS_ACTIVITY, {
      event_type: 'payment_blocked',
      reason: 'excessive_failed_attempts',
      failed_attempts: sessionData.failedAttempts,
      session_id: sessionData.sessionId
    });
  }
};

/**
 * Reset failed attempts counter
 * Call this on successful payment
 */
export const resetFailedAttempts = () => {
  const sessionData = getInteractionSessionData();
  sessionData.failedAttempts = 0;
  sessionData.failedAttemptTimestamps = [];
  saveInteractionSessionData(sessionData);
};

/**
 * Calculate risk level based on session data
 */
const calculateRiskLevel = (sessionData) => {
  const criticalPatterns = sessionData.suspiciousEvents
    .flatMap(e => e.patterns)
    .filter(p => p.severity === 'critical').length;
  
  const highPatterns = sessionData.suspiciousEvents
    .flatMap(e => e.patterns)
    .filter(p => p.severity === 'high').length;
  
  if (criticalPatterns > 0) return 'critical';
  if (highPatterns >= 2) return 'high';
  if (sessionData.suspiciousEvents.length > 0) return 'medium';
  return 'low';
};

/**
 * Log payment security summary to DataDog
 * Call this before form submission
 */
export const logPaymentSecuritySummary = () => {
  const sessionData = getInteractionSessionData();
  const summary = getSessionSummary(sessionData);
  
  addErrorInDatadogRum({
    type: 'payment_security_summary',
    ...summary,
    suspicious_patterns: sessionData.suspiciousEvents.map(e => e.patterns).flat(),
    risk_level: calculateRiskLevel(sessionData)
  });
};

// ============================================================================
// END PAYMENT PAGE INTERACTION TRACKING
// ============================================================================

/**
 * Send security events to server for centralized logging and analysis using GraphQL
 */
const sendToServerLog = async (logEntry) => {
  try {
    await createClient.mutate({
      mutation: LogPaymentSecurityEvent,
      variables: {
        input: {
          timestamp: logEntry.timestamp,
          eventType: logEntry.event_type,
          eventData: JSON.stringify(logEntry),
          source: logEntry.source,
          sessionId: logEntry.sessionId || 'unknown',
          userAgent: logEntry.browserFingerprint?.userAgent || navigator.userAgent,
          isSuspicious: logEntry.is_suspicious || false,
          suspiciousPatterns: logEntry.suspicious_patterns ? JSON.stringify(logEntry.suspicious_patterns) : null,
        }
      },
      // Don't update cache for logging mutations
      fetchPolicy: 'no-cache',
    });
  } catch (error) {
    // Silently fail - don't interrupt payment flow
    // Log to DataDog only for monitoring
    addErrorInDatadogRum(error);
  }
};

// Track payment attempt patterns in session storage
const STORAGE_KEYS = {
  PAYMENT_ATTEMPTS: 'payment_attempts_session',
  FAILURE_COUNT: 'payment_failures_session',
  LAST_ATTEMPT_TIME: 'last_payment_attempt',
  USED_TOKENS: 'used_chargify_tokens',
  TOKEN_USAGE_HISTORY: 'token_usage_history'
};

/**
 * Get current session payment data
 */
const getSessionPaymentData = () => {
  try {
    const attempts = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.PAYMENT_ATTEMPTS) || '[]');
    const failureCount = parseInt(sessionStorage.getItem(STORAGE_KEYS.FAILURE_COUNT) || '0', 10);
    const lastAttemptTime = sessionStorage.getItem(STORAGE_KEYS.LAST_ATTEMPT_TIME);

    return {
      attempts,
      failureCount,
      lastAttemptTime: lastAttemptTime ? new Date(lastAttemptTime) : null
    };
  } catch (error) {
    addErrorInDatadogRum(error);
    return { attempts: [], failureCount: 0, lastAttemptTime: null };
  }
};

/**
 * Update session payment data
 */
const updateSessionPaymentData = (attempts, failureCount) => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.PAYMENT_ATTEMPTS, JSON.stringify(attempts));
    sessionStorage.setItem(STORAGE_KEYS.FAILURE_COUNT, failureCount.toString());
    sessionStorage.setItem(STORAGE_KEYS.LAST_ATTEMPT_TIME, new Date().toISOString());
  } catch (error) {
    addErrorInDatadogRum(error);
  }
};

/**
 * Get token usage data from localStorage (persistent across sessions)
 */
const getTokenUsageData = () => {
  try {
    const usedTokens = JSON.parse(localStorage.getItem(STORAGE_KEYS.USED_TOKENS) || '{}');
    const tokenHistory = JSON.parse(localStorage.getItem(STORAGE_KEYS.TOKEN_USAGE_HISTORY) || '[]');
    return { usedTokens, tokenHistory };
  } catch (error) {
    addErrorInDatadogRum(error);
    return { usedTokens: {}, tokenHistory: [] };
  }
};

/**
 * Update token usage data in localStorage
 */
const updateTokenUsageData = (usedTokens, tokenHistory) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USED_TOKENS, JSON.stringify(usedTokens));
    localStorage.setItem(STORAGE_KEYS.TOKEN_USAGE_HISTORY, JSON.stringify(tokenHistory));
  } catch (error) {
    addErrorInDatadogRum(error);
  }
};

/**
 * Generate a hash of the token for tracking (first 8 and last 8 characters)
 * This allows us to track reuse without storing the full token
 */
const generateTokenHash = (token) => {
  if (!token || typeof token !== 'string' || token.length < 16) {
    return null;
  }

  // Create a simple hash using first 8 and last 8 characters
  // This is sufficient for detecting reuse while maintaining some security
  return `${token.substring(0, 8)}...${token.substring(token.length - 8)}`;
};

/**
 * Check if a Chargify token has been used before
 */
const checkTokenReuse = (chargifyToken) => {
  if (!chargifyToken) return { isReused: false, previousUsage: null };

  const tokenHash = generateTokenHash(chargifyToken);
  if (!tokenHash) return { isReused: false, previousUsage: null };

  const { usedTokens, tokenHistory } = getTokenUsageData();

  const previousUsage = usedTokens[tokenHash];
  const isReused = !!previousUsage;

  // Get usage history for this token
  const tokenUsageHistory = tokenHistory.filter(entry => entry.tokenHash === tokenHash);

  return {
    isReused,
    previousUsage,
    usageCount: tokenUsageHistory.length,
    usageHistory: tokenUsageHistory,
    tokenHash
  };
};

/**
 * Record token usage for future reuse detection
 */
const recordTokenUsage = (chargifyToken, success = false, additionalData = {}) => {
  if (!chargifyToken) return;

  const tokenHash = generateTokenHash(chargifyToken);
  if (!tokenHash) return;

  const { usedTokens, tokenHistory } = getTokenUsageData();
  const timestamp = new Date().toISOString();

  // Update the main token record
  usedTokens[tokenHash] = {
    firstUsed: usedTokens[tokenHash]?.firstUsed || timestamp,
    lastUsed: timestamp,
    usageCount: (usedTokens[tokenHash]?.usageCount || 0) + 1,
    successfulUses: (usedTokens[tokenHash]?.successfulUses || 0) + (success ? 1 : 0),
    failedUses: (usedTokens[tokenHash]?.failedUses || 0) + (success ? 0 : 1)
  };

  // Add to usage history
  const historyEntry = {
    tokenHash,
    timestamp,
    success,
    userAgent: navigator.userAgent,
    sessionId: sessionStorage.getItem('session_id') || 'unknown',
    ...additionalData
  };

  tokenHistory.push(historyEntry);

  // Keep only last 100 history entries to prevent storage bloat
  if (tokenHistory.length > 100) {
    tokenHistory.splice(0, tokenHistory.length - 100);
  }

  updateTokenUsageData(usedTokens, tokenHistory);

  return {
    tokenHash,
    usageCount: usedTokens[tokenHash].usageCount,
    isFirstUse: usedTokens[tokenHash].usageCount === 1
  };
};

/**
 * Detect suspicious payment patterns
 */
const detectSuspiciousActivity = (sessionData) => {
  const { attempts, failureCount, lastAttemptTime } = sessionData;
  const suspiciousPatterns = [];

  // Pattern 1: Rapid successive attempts (< 30 seconds between attempts)
  if (lastAttemptTime) {
    const timeDiff = (new Date() - lastAttemptTime) / 1000;
    if (timeDiff < 30) {
      suspiciousPatterns.push({
        type: 'rapid_attempts',
        details: `${timeDiff}s between attempts`,
        severity: 'medium'
      });
    }
  }

  // Pattern 2: Multiple failures in short time (>3 failures in 10 minutes)
  const recentFailures = attempts.filter(attempt => {
    const attemptTime = new Date(attempt.timestamp);
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    return attemptTime > tenMinutesAgo && !attempt.success;
  });

  if (recentFailures.length >= 3) {
    suspiciousPatterns.push({
      type: 'multiple_failures',
      details: `${recentFailures.length} failures in 10 minutes`,
      severity: 'high'
    });
  }

  // Pattern 3: High total failure count (>5 failures in session)
  if (failureCount >= 5) {
    suspiciousPatterns.push({
      type: 'high_failure_count',
      details: `${failureCount} total failures in session`,
      severity: 'high'
    });
  }

  // Pattern 4: Different card patterns (potential card testing)
  const cardPatterns = attempts.map(attempt => attempt.cardPattern).filter(Boolean);
  const uniqueCardPatterns = [...new Set(cardPatterns)];
  if (uniqueCardPatterns.length >= 3) {
    suspiciousPatterns.push({
      type: 'multiple_cards',
      details: `${uniqueCardPatterns.length} different card patterns attempted`,
      severity: 'high'
    });
  }

  return suspiciousPatterns;
};

/**
 * Extract card pattern for tracking (first 6 and last 4 digits)
 */
const extractCardPattern = (cardNumber) => {
  if (!cardNumber || typeof cardNumber !== 'string') return null;

  // Remove spaces and non-digits
  const cleanCard = cardNumber.replace(/\D/g, '');

  if (cleanCard.length < 10) return null;

  // Return first 6 and last 4 digits pattern
  return `${cleanCard.substring(0, 6)}****${cleanCard.substring(cleanCard.length - 4)}`;
};

/**
 * Get user agent and browser fingerprint data
 */
const getBrowserFingerprint = () => {
  try {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    return { error: 'Unable to collect browser data' };
  }
};

/**
 * Track payment attempt (called before payment processing)
 */
export const trackPaymentAttempt = (paymentData = {}, securityToken, subdomain, publicKey) => {
  try {
    const sessionData = getSessionPaymentData();
    const browserFingerprint = getBrowserFingerprint();

    const hashedSecurityToken = generateTokenHash(securityToken);

    const attemptData = {
      timestamp: new Date().toISOString(),
      paymentType: paymentData.paymentType || 'CreditCard',
      cardPattern: paymentData.cardNumber ? extractCardPattern(paymentData.cardNumber) : null,
      hashedSecurityToken,
      subdomain,
      publicKey,
      country: paymentData.country,
      browserFingerprint,
      sessionId: sessionStorage.getItem('session_id') || 'unknown'
    };

    // Check for suspicious patterns
    const suspiciousPatterns = detectSuspiciousActivity(sessionData);

    // Log payment attempt
    logSecurityEvent(PAYMENT_SECURITY_EVENTS.PAYMENT_ATTEMPT, {
      ...attemptData,
      session_failure_count: sessionData.failureCount,
      session_attempt_count: sessionData.attempts.length + 1,
      suspicious_patterns: suspiciousPatterns.length > 0 ? suspiciousPatterns : null,
      is_suspicious: suspiciousPatterns.length > 0
    });

    // Log suspicious activity separately if detected
    if (suspiciousPatterns.length > 0) {
      logSecurityEvent(PAYMENT_SECURITY_EVENTS.SUSPICIOUS_ACTIVITY, {
        patterns: suspiciousPatterns,
        session_data: {
          total_attempts: sessionData.attempts.length + 1,
          total_failures: sessionData.failureCount,
          session_duration_minutes: sessionData.attempts.length > 0 ?
            (new Date() - new Date(sessionData.attempts[0].timestamp)) / (1000 * 60) : 0
        },
        ...attemptData,
        severity_level: suspiciousPatterns.some(p => p.severity === 'high') ? 'high' : 'medium'
      });
    }

    return attemptData;
  } catch (error) {
    addErrorInDatadogRum(error);
    // eslint-disable-next-line no-console
    console.error('Payment security tracking error:', error);
  }
};

/**
 * Track successful payment
 */
export const trackPaymentSuccess = (paymentData = {}, attemptData = {}) => {
  try {
    const sessionData = getSessionPaymentData();

    // Check for token reuse if token is present
    let tokenReuseData = null;
    if (paymentData.chargifyToken) {
      const reuseCheck = checkTokenReuse(paymentData.chargifyToken);

      if (reuseCheck.isReused) {
        // Log token reuse detection
        logSecurityEvent(PAYMENT_SECURITY_EVENTS.TOKEN_REUSE_DETECTED, {
          tokenHash: reuseCheck.tokenHash,
          previousUsageCount: reuseCheck.usageCount,
          firstUsed: reuseCheck.previousUsage?.firstUsed,
          lastUsed: reuseCheck.previousUsage?.lastUsed,
          previousSuccessfulUses: reuseCheck.previousUsage?.successfulUses || 0,
          previousFailedUses: reuseCheck.previousUsage?.failedUses || 0,
          usageHistory: reuseCheck.usageHistory,
          severity: reuseCheck.usageCount > 2 ? 'high' : 'medium',
          timestamp: new Date().toISOString()
        });
      }

      // Record this token usage
      const tokenUsage = recordTokenUsage(paymentData.chargifyToken, true, {
        paymentType: paymentData.paymentType,
        processingTime: paymentData.processingTime,
        country: attemptData.country
      });

      tokenReuseData = {
        tokenHash: reuseCheck.tokenHash,
        isReused: reuseCheck.isReused,
        totalUsageCount: tokenUsage?.usageCount || 1,
        isFirstUse: tokenUsage?.isFirstUse || false
      };
    }

    const successData = {
      ...attemptData,
      success: true,
      chargifyToken: paymentData.chargifyToken ? 'present' : 'missing',
      processingTime: paymentData.processingTime,
      timestamp: new Date().toISOString(),
      tokenReuse: tokenReuseData
    };

    // Update session data
    const updatedAttempts = [...sessionData.attempts, successData];
    updateSessionPaymentData(updatedAttempts, sessionData.failureCount);

    // Log success event
    logSecurityEvent(PAYMENT_SECURITY_EVENTS.PAYMENT_SUCCESS, {
      ...successData,
      session_failure_count: sessionData.failureCount,
      session_attempt_count: updatedAttempts.length,
      attempts_before_success: sessionData.failureCount,
      total_session_time_minutes: sessionData.attempts.length > 0 ?
        (new Date() - new Date(sessionData.attempts[0].timestamp)) / (1000 * 60) : 0
    });

    // Clear failure tracking on success (but keep attempt history)
    sessionStorage.setItem(STORAGE_KEYS.FAILURE_COUNT, '0');

  } catch (error) {
    addErrorInDatadogRum(error);
    // eslint-disable-next-line no-console
    console.error('Payment success tracking error:', error);
  }
};

/**
 * Track payment failure
 */
export const trackPaymentFailure = (errorData = {}, attemptData = {}) => {
  try {
    const sessionData = getSessionPaymentData();
    const newFailureCount = sessionData.failureCount + 1;

    const failureData = {
      ...attemptData,
      success: false,
      error: {
        message: errorData.message || 'Unknown error',
        type: errorData.type || 'unknown',
        code: errorData.code,
        is3DS: errorData.is3DS || false,
        isProcessorDeclined: errorData.isProcessorDeclined || false,
        originalError: errorData.originalError
      },
      timestamp: new Date().toISOString()
    };

    // Update session data
    const updatedAttempts = [...sessionData.attempts, failureData];
    updateSessionPaymentData(updatedAttempts, newFailureCount);

    // Detect patterns specific to this failure
    const suspiciousPatterns = detectSuspiciousActivity(
      { ...sessionData, failureCount: newFailureCount }
    );

    // Log failure event
    logSecurityEvent(PAYMENT_SECURITY_EVENTS.PAYMENT_FAILURE, {
      ...failureData,
      session_failure_count: newFailureCount,
      session_attempt_count: updatedAttempts.length,
      consecutive_failures: newFailureCount,
      suspicious_patterns: suspiciousPatterns.length > 0 ? suspiciousPatterns : null,
      is_suspicious: suspiciousPatterns.length > 0
    });

    // Track rapid attempts separately
    if (suspiciousPatterns.some(p => p.type === 'rapid_attempts')) {
      logSecurityEvent(PAYMENT_SECURITY_EVENTS.RAPID_PAYMENT_ATTEMPTS, {
        failure_count: newFailureCount,
        time_between_attempts: suspiciousPatterns.find(p => p.type === 'rapid_attempts')?.details,
        ...failureData
      });
    }

    // Track multiple card failures
    if (suspiciousPatterns.some(p => p.type === 'multiple_cards')) {
      logSecurityEvent(PAYMENT_SECURITY_EVENTS.MULTIPLE_CARD_FAILURES, {
        unique_cards_attempted: suspiciousPatterns.find(p => p.type === 'multiple_cards')?.details,
        failure_count: newFailureCount,
        ...failureData
      });
    }

  } catch (error) {
    addErrorInDatadogRum(error);
    // eslint-disable-next-line no-console
    console.error('Payment failure tracking error:', error);
  }
};

/**
 * Clear session payment tracking data (call on successful checkout completion)
 */
export const clearPaymentTrackingSession = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.PAYMENT_ATTEMPTS);
    sessionStorage.removeItem(STORAGE_KEYS.FAILURE_COUNT);
    sessionStorage.removeItem(STORAGE_KEYS.LAST_ATTEMPT_TIME);
  } catch (error) {
    addErrorInDatadogRum(error);
  }
};

/**
 * Track token reuse attempt (when someone tries to reuse a token)
 * This should be called whenever a Chargify token is about to be used
 */
export const trackTokenReuseAttempt = (chargifyToken, context = {}) => {
  try {
    if (!chargifyToken) return { isReused: false };

    const reuseCheck = checkTokenReuse(chargifyToken);

    if (reuseCheck.isReused) {
      // Log the reuse attempt
      logSecurityEvent(PAYMENT_SECURITY_EVENTS.TOKEN_REUSE_ATTEMPT, {
        tokenHash: reuseCheck.tokenHash,
        previousUsageCount: reuseCheck.usageCount,
        firstUsed: reuseCheck.previousUsage?.firstUsed,
        lastUsed: reuseCheck.previousUsage?.lastUsed,
        previousSuccessfulUses: reuseCheck.previousUsage?.successfulUses || 0,
        previousFailedUses: reuseCheck.previousUsage?.failedUses || 0,
        timeSinceLastUse: reuseCheck.previousUsage?.lastUsed ?
          (new Date() - new Date(reuseCheck.previousUsage.lastUsed)) / (1000 * 60) : null, // minutes
        context,
        severity: reuseCheck.usageCount > 2 ? 'high' : 'medium',
        timestamp: new Date().toISOString()
      });

      // Record this attempt (even before we know if it will succeed)
      recordTokenUsage(chargifyToken, false, {
        ...context,
        attemptType: 'reuse_attempt'
      });
    }

    return {
      isReused: reuseCheck.isReused,
      tokenHash: reuseCheck.tokenHash,
      usageCount: reuseCheck.usageCount,
      riskLevel: reuseCheck.usageCount > 2 ? 'high' : reuseCheck.isReused ? 'medium' : 'low'
    };

  } catch (error) {
    addErrorInDatadogRum(error);
    // eslint-disable-next-line no-console
    console.error('Token reuse tracking error:', error);
    return { isReused: false, error: true };
  }
};

/**
 * Get token usage statistics for monitoring
 */
export const getTokenUsageStats = () => {
  try {
    const { usedTokens, tokenHistory } = getTokenUsageData();

    const stats = {
      totalUniqueTokens: Object.keys(usedTokens).length,
      totalUsageEvents: tokenHistory.length,
      reusedTokens: Object.values(usedTokens).filter(token => token.usageCount > 1).length,
      highReuseTokens: Object.values(usedTokens).filter(token => token.usageCount > 3).length,
      successfulTokenUses: Object.values(usedTokens).reduce((sum, token) => sum + token.successfulUses, 0),
      failedTokenUses: Object.values(usedTokens).reduce((sum, token) => sum + token.failedUses, 0)
    };

    stats.reuseRate = stats.totalUniqueTokens > 0 ? (stats.reusedTokens / stats.totalUniqueTokens) * 100 : 0;
    stats.successRate = (stats.successfulTokenUses + stats.failedTokenUses) > 0 ?
      (stats.successfulTokenUses / (stats.successfulTokenUses + stats.failedTokenUses)) * 100 : 0;

    return stats;
  } catch (error) {
    addErrorInDatadogRum(error);
    return { error: 'Unable to calculate token usage stats' };
  }
};

/**
 * Clear token usage data (for testing or privacy compliance)
 */
export const clearTokenUsageData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USED_TOKENS);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_USAGE_HISTORY);
  } catch (error) {
    addErrorInDatadogRum(error);
  }
};

/**
 * Get current session security metrics (for debugging/monitoring)
 */
export const getSessionSecurityMetrics = () => {
  const sessionData = getSessionPaymentData();
  const suspiciousPatterns = detectSuspiciousActivity(sessionData);
  const tokenStats = getTokenUsageStats();

  return {
    ...sessionData,
    suspiciousPatterns,
    isSuspicious: suspiciousPatterns.length > 0,
    riskLevel: suspiciousPatterns.some(p => p.severity === 'high') ? 'high' :
      suspiciousPatterns.some(p => p.severity === 'medium') ? 'medium' : 'low',
    tokenUsageStats: tokenStats
  };
};
